import { useEffect, useState } from 'react';

import { nonLicensorMappingColumns } from 'src/constants/planMappings';
import { IKeyValue, ILicensorAttribute } from 'src/types/IOptions';
import { IPlanMappingsGetResponse, IPlanMappingsPostInput } from 'src/types/IPlanMappings';

export const formatServices = (inputs: IKeyValue[]): string[] => {
    // Put service names into array
    return inputs.map((service) => {
        return service.value;
    });
};

export const formatLicensorNames = (inputs: ILicensorAttribute[]): string[] => {
    // Put unique licensor names into array
    return inputs.reduce((uniqueLicensorArr: string[], licensor: ILicensorAttribute) => {
        if (uniqueLicensorArr.indexOf(licensor.licensorName) === -1) {
            uniqueLicensorArr.push(licensor.licensorName);
        }
        return uniqueLicensorArr;
    }, []);
};

export function isEmptyCell(value: any): boolean {
    if (value == null) {
        return true;
    }

    if (['', '-'].includes(value)) {
        return true;
    }

    return false;
}

export const formatLicensorDisplayNameToHumanReadable = (displayName: string): string => {
    // Take out __ in display name
    const [licensorName, licensorAttribute] = displayName.split('__');
    return `[${licensorName}] ${licensorAttribute}`;
};

export const formatLicensorDisplayNameToCodeReadable = (displayName: string): string => {
    // Take out [] in display name
    displayName = displayName.slice(1);
    const [licensorName, licensorAttribute] = displayName.split(']');
    return `${licensorName.trim()}__${licensorAttribute.trim()}`;
};

export const createLicensorDisplayNameCodeReadable = (name: string, attribute: string): string => {
    return `${name}__${attribute}`;
};

export const createLicensorDisplayNameHumanReadable = (name: string, attribute: string): string => {
    return `[${name}] ${attribute}`;
};

export const isBoolLicensorCodeDisplayName = (displayName: string) => {
    if (!nonLicensorMappingColumns.includes(displayName)) {
        const firstTwoChars = displayName.split('__')[1].slice(0, 2);
        return firstTwoChars.toUpperCase() === 'IS';
    }
    return false;
};

export const isBoolLicensorHumanDisplayName = (displayName: string) => {
    if (!nonLicensorMappingColumns.includes(displayName)) {
        const firstTwoChars = formatLicensorDisplayNameToCodeReadable(displayName).split('__')[1].slice(0, 2);
        return firstTwoChars.toUpperCase() === 'IS';
    }
    return false;
};

export const isEqual = (originalRow: IPlanMappingsGetResponse, newRow: IPlanMappingsPostInput): boolean => {
    // Remove keys in original row without values
    Object.entries(originalRow).forEach(([key, value]) => {
        if (value === '-' || value === '' || value === null) {
            delete originalRow[key];
        }
    });

    // Compare all non-licensor mapping columns and size of licensor mappings
    if (
        originalRow.paperPlanName !== newRow.paperPlanName ||
        originalRow.country !== newRow.country ||
        originalRow.activeDate !== newRow.activeDate ||
        originalRow.endDate !== newRow.endDate ||
        originalRow.isDeleted !== newRow.isDeleted ||
        Object.keys(originalRow).length !== Object.keys(newRow).length
    )
        return false;

    // Compare licensor mapping columns
    Object.entries(originalRow.licensorMappings).forEach(([key, value]) => {
        if (newRow.licensorMappings[key] !== value) return false;
    });

    return true;
};

export type windowSizeType = {
    width: number;
    height: number;
};

export const getWindowSize = () => {
    // Get dynamic window size
    const [windowSize, setWindowSize] = useState<windowSizeType>({
        width: 0,
        height: 0
    });

    useEffect(() => {
        // Handler to call on window resize
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        // Listen for window resize
        window.addEventListener('resize', handleResize);

        // Call handler right away so state gets updated with initial window size
        handleResize();

        // Remove event listener on cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowSize;
};
